import isEqual from 'lodash/fp/isEqual';
import queryString from 'query-string';
import { push, replace } from '../../../navigation/actions';
import * as selectNavigation from '../../../navigation/selectors';
import { RouteHookBody } from '../../../navigation/types';
import { ActionHookBody } from '../../../types';
import { actionHook } from '../../../utils';
import { assertDefined } from '../../../utils/assertions';
import { openToc } from '../../actions';
import { content } from '../../routes';
import * as selectContent from '../../selectors';
import { findArchiveTreeNodeById } from '../../utils/archiveTreeUtils';
import { stripIdVersion } from '../../utils/idUtils';
import { createNavigationMatch } from '../../utils/navigationUtils';
import { clearSearch, openSearchInSidebar, receiveSearchResults, requestSearch, selectSearchResult } from '../actions';
import { isSearchScrollTarget } from '../guards';
import * as select from '../selectors';
import { findSearchResultHit, getFirstResult, getIndexData } from '../utils';
import trackSearch from './trackSearch';

export const requestSearchHook: ActionHookBody<typeof requestSearch> = (services) => async({payload, meta}) => {
  const state = services.getState();
  const book = selectContent.book(state);

  if (!book || !payload) {
    return;
  }

  const results = await services.searchClient.search({
    books: [`${book.archiveVersion}/${book.id}@${book.contentVersion}`],
    indexStrategy: 'i1',
    q: payload,
    searchStrategy: 's1',
  });

  services.dispatch(receiveSearchResults(results, meta));
};

export const receiveSearchHook: ActionHookBody<typeof receiveSearchResults> = (services) => ({payload, meta}) => {
  const state = services.getState();
  const {page: currentPage, book} = selectContent.bookAndPage(state);
  const pageIsLoading = selectContent.loadingPage(state);
  const query = select.query(state);
  const results = select.hits(state);
  const systemQueryParams = selectNavigation.systemQueryParameters(state);
  const persistentQueryParams = selectNavigation.persistentQueryParameters(state);

  if (pageIsLoading || !book) {
    return; // book changed while query was in the air
  }

  const searchResultHit = meta && meta.searchScrollTarget &&
    findSearchResultHit(results, meta.searchScrollTarget, state?.content?.page?.id);
  const selectedResult = searchResultHit && meta.searchScrollTarget
    ? {result: searchResultHit, highlight: meta.searchScrollTarget.index}
    : getFirstResult(book, payload);

  if (
    selectedResult
    // We are clearing selected result when requesting a new search so in the theory this should never happen
    && (isEqual(select.selectedResult(state), selectedResult)
      // selectedResult bookId data is different than current book id
      || book.id !== getIndexData(selectedResult.result.index).bookId)
  ) {
    return;
  }

  if (selectedResult) {
    services.dispatch(selectSearchResult(selectedResult));
  }

  const targetPageId = selectedResult?.result.source.pageId || currentPage?.id;

  const action = (targetPageId && stripIdVersion(targetPageId)) === (currentPage && stripIdVersion(currentPage.id))
    ? replace : push;

  const options = selectedResult
    ? {
      hash: selectedResult.result.source.elementId,
      search: queryString.stringify({
        modal: persistentQueryParams.modal,
        query,
        target: JSON.stringify({ type: 'search', index: selectedResult.highlight }),
        ...systemQueryParams,
      }),
    }
    : { search: queryString.stringify({ query, modal: persistentQueryParams.modal }) };

  const navigationMatch = targetPageId
    ? createNavigationMatch(
      assertDefined(
        findArchiveTreeNodeById(book.tree, targetPageId),
        'search result pointed to page that wasn\'t in book'
      ), book)
    : selectNavigation.match(state);

  if (navigationMatch) {
    services.dispatch(action(navigationMatch, options));
  }
};

export const clearSearchHook: ActionHookBody<typeof clearSearch | typeof openToc> = (services) => () => {
  const state = services.getState();
  const scrollTarget = selectNavigation.scrollTarget(state);
  const hash = selectNavigation.hash(state);
  const query = selectNavigation.query(state);

  if (!Object.keys(query).length) {
    return;
  }

  const systemQueryParams = selectNavigation.systemQueryParameters(state);
  const persistentQueryParams = selectNavigation.persistentQueryParameters(state);
  const newTarget = scrollTarget && isSearchScrollTarget(scrollTarget) ? '' : persistentQueryParams.target;
  const newPersistentParams = {...persistentQueryParams, query: undefined, target: newTarget || undefined};

  services.history.replace({
    hash: scrollTarget && isSearchScrollTarget(scrollTarget) ? '' : hash,
    search: queryString.stringify(systemQueryParams) + queryString.stringify(newPersistentParams),
  });
};

// composed in /content/locationChange hook because it needs to happen after book load
export const syncSearch: RouteHookBody<typeof content> = (services) => async() => {
  const state = services.getState();
  const navigationQuery = selectNavigation.query(state).query;
  const searchQuery = select.query(state);
  const scrollTarget = selectNavigation.scrollTarget(state);
  const searchScrollTarget = scrollTarget && isSearchScrollTarget(scrollTarget) ? scrollTarget : null;
  const searchHits = select.hits(state);
  const targettedHit = searchScrollTarget &&
    findSearchResultHit(searchHits, searchScrollTarget, state?.content?.page?.id);
  const navigationSelectedResult = targettedHit && searchScrollTarget
    ? { result: targettedHit, highlight: searchScrollTarget.index }
    : null;
  const currentSelectedResult = select.selectedResult(state);

  if (typeof navigationQuery === 'string' && navigationQuery !== searchQuery) {
    services.dispatch(requestSearch(
      navigationQuery,
      searchScrollTarget ? { isResultReload: true, searchScrollTarget } : undefined
    ));
  } else if (
    navigationSelectedResult
    && (!currentSelectedResult || !isEqual(navigationSelectedResult, currentSelectedResult))
  ) {
    services.dispatch(selectSearchResult(navigationSelectedResult));
  }
};

// Search in sidebar experiment
export const openSearchInSidebarHook: ActionHookBody<typeof openSearchInSidebar> = (services) => () => {
  // Restore search state when opening sidebar
  const state = services.getState();
  const previousState = select.previousState(state);
  const hash = selectNavigation.hash(state);
  const systemQueryParams = selectNavigation.systemQueryParameters(state);
  const { query, selectedResult } = previousState;

  if (!query) {
    return;
  }

  const options = selectedResult
    ? {
      hash: selectedResult.result.source.elementId,
      search: queryString.stringify({
        query,
        target: JSON.stringify({ type: 'search', index: selectedResult.highlight }),
        ...systemQueryParams,
      }),
    }
    : { search: queryString.stringify({ query }) };

  services.history.replace({
    hash,
    ...options,
  });
};

export default [
  trackSearch,
  actionHook(clearSearch, clearSearchHook),
  actionHook(openToc, clearSearchHook),
  actionHook(openSearchInSidebar, openSearchInSidebarHook),
  actionHook(requestSearch, requestSearchHook),
  actionHook(receiveSearchResults, receiveSearchHook),
];
